import { Analytics } from '@cobliteam/analytics';
import type { Preferences } from '@cobliteam/common-types/src/preferences';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useAnnouncement } from '@cobliteam/hooks/src/useEntity/usePrismic';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import { type Permission, usePermissions } from '@cobliteam/permissions';
import { PrismicLink, PrismicRichText } from '@cobliteam/prismic';
import { Button } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { camelCase } from 'change-case';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DialogTypeformSurvey from '../../dialogs/DialogTypeformSurvey/DialogTypeformSurvey';
import { InfoCard, InfoCardContent, InfoCardDescription, InfoCardDismiss, InfoCardFooter, InfoCardMedia, InfoCardTitle } from '../../InfoCard';

const OFFSET_FACTOR = 5.5;
const SCALE_FACTOR = 0.03;
const OPACITY_FACTOR = 0.1;
const STORAGE_PREFIX = 'cobli-announcement-';

// Helper function to extract dismissed announcement IDs from preferences
const getDismissedAnnouncementIds = (preferences: Preferences | null): string[] => {
  if (!preferences) return [];

  return Object.keys(preferences)
    .filter((key) => key.startsWith(STORAGE_PREFIX))
    .map((key) => key.replace(STORAGE_PREFIX, ''));
};

// Helper function to check if announcement meets feature flag requirements
const meetsFeatureFlagRequirements = (announcement: any, featureFlags: any): boolean => {
  const requiredFeatureFlags = announcement.data.feature_flags || ([] as string[]);

  return requiredFeatureFlags.every(({ feature_flag }: { feature_flag: string }) => {
    if (!feature_flag) return false;

    const flagKey = camelCase(feature_flag) as keyof typeof featureFlags;
    return featureFlags[flagKey] === true;
  });
};

// Helper function to check if announcement meets runtime flag requirements
const meetsRuntimeFlagRequirements = (announcement: any, runtimeFlags: any): boolean => {
  const requiredRuntimeFlags = announcement.data.runtime_flags || [];

  return requiredRuntimeFlags.every(({ runtime_flag }: { runtime_flag: string }) => {
    if (!runtime_flag) return false;

    const flagKey = runtime_flag as keyof typeof runtimeFlags;
    return runtimeFlags[flagKey] === true;
  });
};

// Helper function to check if announcement meets page route requirements
const meetsPageRouteRequirements = (announcement: any, currentPathname: string): boolean => {
  const requiredPageRoutes = announcement.data.pages || [];
  if (requiredPageRoutes.length === 0) return true; // No page restriction

  return requiredPageRoutes.some(({ page_route }: { page_route: string }) => {
    if (!page_route) return false;
    return currentPathname.startsWith(page_route);
  });
};

// Helper function to check if announcement meets permission requirements
const meetsPermissionRequirements = (announcement: any, hasAllPermissions: (permissions: Permission[]) => boolean): boolean => {
  const requiredPermissions = announcement.data.permissions || [];
  if (requiredPermissions.length === 0) return true; // No permission restriction

  const permissionsKeys = announcement.data.permissions.map(({ permission }: { permission: string }) => permission) as Permission[];

  return hasAllPermissions(permissionsKeys);
};

// Helper function to sort announcements by priority
const sortAnnouncementsByPriority = (announcements: any[]): any[] => {
  return announcements.sort((a, b) => {
    // Sort by pages first (fewer pages = higher priority)
    const aPages = a.data.pages || [];
    const bPages = b.data.pages || [];
    return aPages.length - bPages.length || a.id.localeCompare(b.id);
  });
};

const isTypeformLink = (link: string): boolean => {
  return link.includes('typeform.com');
};
/**
 * Extrai o ID do Typeform de um link, se presente.
 * @param link - O link do Typeform.
 * @returns O ID do Typeform como string, ou null se não encontrado.
 * @example
 *   getTypeformIdFromLink('https://form.typeform.com/to/oBmT1sU3'); // retorna 'oBmT1sU3'
 */
const getTypeformIdFromLink = (link: string): string | null => {
  if (!isTypeformLink(link)) return null;

  // Find typeform id in the URL pattern
  // Examples:
  // https://form.typeform.com/to/abcdef
  // https://cobli.typeform.com/to/abcdef
  // https://xyz.typeform.com/to/abcdef#someparam=something
  const match = link.match(/typeform\.com\/to\/([\w\d]+)/i);
  return match?.[1] || null;
};

// Helper function to calculate CSS variables for card positioning
const calculateCardPositioning = (index: number, totalCards: number) => {
  const cardsFromTop = totalCards - (index + 1);

  return {
    ['--scale' as string]: 1 - cardsFromTop * SCALE_FACTOR,
    ['--y' as string]: `-${cardsFromTop * OFFSET_FACTOR}%`,
    ['--opacity' as string]: 1 - cardsFromTop * OPACITY_FACTOR,
  };
};

// Helper function to create storage key for dismissed announcements
const createDismissedStorageKey = (announcementId: string): string => {
  return `${STORAGE_PREFIX}${announcementId}`;
};

const AppAnnouncementCard = () => {
  const featureFlags = useFeatureFlags();
  const { hasAllPermissions } = usePermissions();
  const { runtimeFlags } = useDashboard();
  const [openTypeformSurvey, setOpenTypeformSurvey] = useState<string | null>(null);

  const location = useLocation();
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');
  const { data: announcements, isLoading, isPending, error } = useAnnouncement({ options: { enabled: featureFlags?.announcementCard } });

  const dismissedIds = useMemo(() => {
    return getDismissedAnnouncementIds(preferences);
  }, [preferences]);

  const cards = useMemo(() => {
    if (!announcements) return [];

    const filteredCards = announcements
      .filter((item) => !dismissedIds.includes(item.id)) // Remove dismissed announcements
      .filter((item) => meetsFeatureFlagRequirements(item, featureFlags))
      .filter((item) => meetsRuntimeFlagRequirements(item, runtimeFlags))
      .filter((item) => meetsPageRouteRequirements(item, location.pathname))
      .filter((item) => meetsPermissionRequirements(item, hasAllPermissions));

    const sortedCards = sortAnnouncementsByPriority(filteredCards);

    // Track when cards are displayed
    if (sortedCards.length > 0) {
      Analytics.track('AppAnnouncement - Display', {
        cardCount: sortedCards.length,
        cardIds: sortedCards.map((card) => card.id),
        cardTitles: sortedCards.map((card) => card.data.title?.[0]?.text || ''),
      });
    }

    return sortedCards;
  }, [dismissedIds, featureFlags, announcements, runtimeFlags, location, hasAllPermissions]);

  const onDismiss = (id: string, title?: string) => {
    Analytics.track('AppAnnouncement - Dismiss', {
      announcementId: id,
      title: title || '',
    });

    const storageKey = createDismissedStorageKey(id);
    setPreferences({
      ...preferences,
      [storageKey]: true,
    });
  };

  if (!featureFlags?.announcementCard) return null;
  if (isLoading || isPending) return null;
  if (error) return null;

  return (
    <div className='fixed right-16 bottom-6 z-announcement w-[362px] rounded-xl'>
      {cards.map(({ data: { title, description, imagem, link, link_text }, id }, index) => {
        const isOnDisplay = index === cards.length - 1;
        const cssVars = calculateCardPositioning(index, cards.length);

        // Link if typeform add custom parameter to the link
        const hasTypeformLink = isTypeformLink(link.url);
        const typeformId = hasTypeformLink ? getTypeformIdFromLink(link.url) : null;
        return (
          <div
            key={id}
            className={cn(
              'translate-y-[var(--y)] scale-[var(--scale)] opacity-[var(--opacity)] transition-[transform,height] duration-200',
              !isOnDisplay && 'absolute top-0 h-[200px] overflow-hidden'
            )}
            style={cssVars}
          >
            <InfoCard shineBorder={isOnDisplay}>
              <InfoCardContent>
                <InfoCardDismiss onDismiss={() => onDismiss(id, title?.[0]?.text)}>
                  <Button variant='ghost' color='default' size='sm' icon={<Icon name='x' size='xs' />} className='absolute top-1.5 right-1.5' />
                </InfoCardDismiss>
                <InfoCardTitle>
                  <PrismicRichText field={title} className='line-clamp-2 font-semibold text-primary' />
                </InfoCardTitle>
                <InfoCardDescription>
                  <PrismicRichText field={description} className='line-clamp-3 text-default' />
                </InfoCardDescription>
                <InfoCardMedia
                  media={[{ type: 'image', src: imagem.url || '', alt: imagem.alt || '', className: 'h-[200px] w-full' }]}
                  loading='lazy'
                  shrinkHeight={75}
                  expandHeight={150}
                />
                <InfoCardFooter className='flex justify-end'>
                  {typeformId ? (
                    <>
                      <Button
                        size='sm'
                        variant='link'
                        color='primary'
                        onClick={() => {
                          Analytics.track('AppAnnouncement - Link Click', {
                            announcementId: id,
                            title: title?.[0]?.text || '',
                            linkText: link_text,
                          });
                          setOpenTypeformSurvey(typeformId);
                        }}
                      >
                        {link_text} <Icon name='arrow-up-right' className='ml-1' size='xs' />
                      </Button>
                      <DialogTypeformSurvey typeformId={typeformId} open={openTypeformSurvey === typeformId} onClose={() => setOpenTypeformSurvey(null)} />
                    </>
                  ) : (
                    <PrismicLink field={link} target='_blank'>
                      <Button
                        size='sm'
                        variant='link'
                        color='primary'
                        onClick={() => {
                          Analytics.track('AppAnnouncement - Link Click', {
                            announcementId: id,
                            title: title?.[0]?.text || '',
                            linkText: link_text,
                          });
                        }}
                      >
                        {link_text} <Icon name='arrow-up-right' className='ml-1' size='xs' />
                      </Button>
                    </PrismicLink>
                  )}
                </InfoCardFooter>
              </InfoCardContent>
            </InfoCard>
          </div>
        );
      })}
    </div>
  );
};

export default AppAnnouncementCard;
